// frontend/src/components/ProfileSettings.js
import React, { useState, useContext, useRef, useEffect } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { trackAdminUserPromoted } from '../utils/analytics';
import { Link, useNavigate } from 'react-router-dom';
import {
    UserIcon as User,
    MailIcon as Mail,
    LockIcon as Lock,
    SaveIcon as Save,
    EyeIcon as Eye,
    EyeOffIcon as EyeOff,
    SmileIcon as Smile,
    ShieldIcon as Shield,
    ChevronRightIcon as ChevronRight,
    LinkOffIcon as Unlink,
    LinkIcon as LinkIcon,
    AlertCircleIcon as AlertCircle,
    CheckCircleIcon as CheckCircle,
    CameraIcon as Camera,
    UploadIcon as Upload,
    DeleteIcon as Trash2,
    ViewQuiltIcon as DashboardIcon,
    LocationIcon as MapPin,
    GroupIcon as Users,
    RefreshIcon as Refresh,
    EditIcon as Edit,
    CalendarIcon as Calendar,
    SmartphoneIcon as Phone,
    KeyIcon as Key,
    CheckIcon as Check,
    CloseIcon as Close
} from './Icons';
import CloudflareUserStatus from './CloudflareUserStatus';

const InputWrapper = ({ label, icon: Icon, children }) => (
    <div className="space-y-2 group">
        <label className="text-body-medium font-bold text-surface-onVariant ml-1 flex items-center gap-2 group-focus-within:text-primary transition-colors">
            {Icon && <Icon size={16} />}
            {label}
        </label>
        {children}
    </div>
);

const inputClasses = `
    w-full px-5 py-4 
    bg-surface-containerLowest 
    border-2 border-outline/30 
    rounded-2xl 
    text-surface-on font-medium 
    placeholder:text-surface-onVariant/40 
    focus:border-primary focus:bg-surface focus:outline-none 
    transition-all duration-medium1 
    shadow-sm focus:shadow-elevation-1
`;

const ProfileSettings = ({ profile, onUpdate, isAdminView = false }) => {
    const { API_URL, user } = useContext(AuthContext);
    const isLeaderOrAdmin = user?.role === 'admin' || user?.role === 'gruppenleiter';
    const canManageRoles = user?.role === 'admin';
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Photo upload state
    const [profilePhotoUrl, setProfilePhotoUrl] = useState(profile.userData?.profilePhoto?.url || '');
    const [photos, setPhotos] = useState(profile.userData?.photos || []);
    const [uploading, setUploading] = useState(false);
    const [uploadingGallery, setUploadingGallery] = useState(false);
    const fileInputRef = useRef(null);
    const galleryInputRef = useRef(null);

    // Sync photos from profile when it changes
    useEffect(() => {
        if (profile.userData?.profilePhoto?.url) {
            setProfilePhotoUrl(profile.userData.profilePhoto.url);
        } else if (profile.userData?.photos?.length > 0) {
            // Fallback for migration: use first gallery photo if no profile photo set
            // setProfilePhotoUrl(profile.userData.photos[0].url);
        }
        
        if (profile.userData?.photos) {
            setPhotos(profile.userData.photos);
        }
    }, [profile]);

    const [blocks, setBlocks] = useState([]);
    const [loadingBlocks, setLoadingBlocks] = useState(true);


    useEffect(() => {
        const fetchBlocks = async () => {
            if (!profile?._id || !profile?.userData?.isTeamMember) return;
            try {
                const response = await axios.get(`${API_URL}/team/${profile._id}`);
                if (response.data.profileBlocks) {
                    const sortedBlocks = response.data.profileBlocks.sort((a, b) => a.order - b.order);
                    setBlocks(sortedBlocks);
                }
            } catch (error) {
                console.error('Error fetching profile blocks:', error);
            } finally {
                setLoadingBlocks(false);
            }
        };
        fetchBlocks();
    }, [profile, API_URL]);

    const [formData, setFormData] = useState({
        email: profile.email || '',
        firstName: profile.userData?.firstName || '',
        lastName: profile.userData?.lastName || '',
        scoutName: profile.userData?.scoutName || '',
        phone: profile.userData?.phone || '',
        birthDate: profile.userData?.birthDate ? new Date(profile.userData.birthDate).toISOString().split('T')[0] : '',
        street: profile.userData?.street || '',
        houseNumber: profile.userData?.houseNumber || '',
        zipCode: profile.userData?.zipCode || '',
        city: profile.userData?.city || '',
        job: profile.userData?.job || '',
        shortDescription: profile.userData?.shortDescription || '',
        description: profile.userData?.description || '',
        seitWannDabei: profile.userData?.standardFields?.seitWannDabei || '',
        // Admin fields
        role: profile.role || 'kind',
        warte: Array.isArray(profile.userData?.warte)
            ? profile.userData.warte
            : (profile.userData?.warte ? [profile.userData.warte] : []),
        isApproved: profile.isApproved || false,
        isEmailVerified: profile.isEmailVerified || false,
        isTeamMember: profile.userData?.isTeamMember || false,
        displayName: profile.userData?.displayName || '',

        officialEmail: profile.officialEmail || '',
        job: profile.userData?.job || ''
    });

    const [unlinkGooglePending, setUnlinkGooglePending] = useState(false);

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handlePasswordChange = (e) => {
        setPasswordData({
            ...passwordData,
            [e.target.name]: e.target.value
        });
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user.token}`
                }
            };

            const updateUrl = isAdminView 
                ? `${API_URL}/users/${profile._id}/profile` 
                : `${API_URL}/users/profile`;

            const updateData = {
                email: formData.email,
                officialEmail: formData.officialEmail,
                userData: {
                    ...profile.userData,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phone: formData.phone,
                    birthDate: formData.birthDate,
                    street: formData.street,
                    houseNumber: formData.houseNumber,
                    zipCode: formData.zipCode,
                    city: formData.city,
                    job: formData.job,
                    // scoutName nur senden wenn GL/Admin
                    ...(isLeaderOrAdmin ? { scoutName: formData.scoutName } : {}),
                    ...(canManageRoles ? { warte: formData.warte } : {})
                }
            };

            // Add Admin-only fields
            if (isAdminView && canManageRoles) {
                updateData.role = formData.role;
                updateData.isApproved = formData.isApproved;
                updateData.isEmailVerified = formData.isEmailVerified;

                if (unlinkGooglePending) {
                    updateData.unlinkGoogle = true;
                }
            }

            const { data } = await axios.put(
                updateUrl,
                updateData,
                config
            );

            if (isAdminView && canManageRoles && formData.role !== profile.role) {
                trackAdminUserPromoted(formData.role);
            }

            setMessage({ type: 'success', text: 'Account-Daten erfolgreich aktualisiert!' });
            setUnlinkGooglePending(false);
            if (onUpdate) onUpdate(data);
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.message || 'Fehler beim Aktualisieren des Profils'
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setMessage({ type: 'error', text: 'Die neuen Passwörter stimmen nicht überein!' });
            setLoading(false);
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setMessage({ type: 'error', text: 'Das neue Passwort muss mindestens 6 Zeichen lang sein!' });
            setLoading(false);
            return;
        }

        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user.token}`
                }
            };

            await axios.put(
                `${API_URL}/users/change-password`,
                {
                    currentPassword: passwordData.currentPassword,
                    newPassword: passwordData.newPassword
                },
                config
            );

            setMessage({ type: 'success', text: 'Passwort erfolgreich geändert!' });
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.message || 'Fehler beim Ändern des Passworts'
            });
        } finally {
            setLoading(false);
        }
    };

    const [showAdminPasswordModal, setShowAdminPasswordModal] = useState(false);
    const [adminNewPassword, setAdminNewPassword] = useState('');
    const [showAdminPasswordText, setShowAdminPasswordText] = useState(true);
    const [copiedAdminPassword, setCopiedAdminPassword] = useState(false);

    const generateStrongPassword = () => {
        const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
        const lowercase = 'abcdefghijkmnopqrstuvwxyz';
        const numbers = '23456789';
        const symbols = '!@#$%&*?';
        const allChars = uppercase + lowercase + numbers + symbols;
        
        let pwd = '';
        pwd += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
        pwd += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
        pwd += numbers.charAt(Math.floor(Math.random() * numbers.length));
        pwd += symbols.charAt(Math.floor(Math.random() * symbols.length));
        
        for (let i = 4; i < 12; i++) {
            pwd += allChars.charAt(Math.floor(Math.random() * allChars.length));
        }
        
        // Shuffle password
        pwd = pwd.split('').sort(() => 0.5 - Math.random()).join('');
        setAdminNewPassword(pwd);
        setCopiedAdminPassword(false);
    };

    const handleAdminDirectSetPassword = async (e) => {
        if (e) e.preventDefault();
        if (!adminNewPassword || adminNewPassword.length < 6) {
            setMessage({ type: 'error', text: 'Das Passwort muss mindestens 6 Zeichen lang sein!' });
            return;
        }
        setLoading(true);
        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user.token}`
                }
            };
            const { data } = await axios.put(
                `${API_URL}/users/${profile._id}/set-password`,
                { newPassword: adminNewPassword },
                config
            );
            setMessage({ type: 'success', text: data.message || 'Passwort erfolgreich gesetzt!' });
            setShowAdminPasswordModal(false);
            setAdminNewPassword('');
        } catch (error) {
            setMessage({
                type: 'error',
                text: error.response?.data?.message || 'Fehler beim Setzen des Passworts'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAdminResetPassword = async () => {
        if (!window.confirm('Passwort-Zurücksetzen-Link wirklich an diesen Benutzer senden?')) return;
        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.post(`${API_URL}/users/admin-send-reset-link`, { email: profile.email }, config);
            setMessage({ type: 'success', text: 'Passwort-Zurücksetzen-Link wurde gesendet!' });
        } catch (error) {
            setMessage({ 
                type: 'error', 
                text: error.response?.data?.message || 'Fehler beim Senden des Links' 
            });
        } finally {
            setLoading(false);
        }
    };
    
    const handleDeleteUser = async () => {
        if (!window.confirm(`Möchtest du den Benutzer "${profile.userData?.scoutName || profile.email}" wirklich UNWIDERRUFLICH löschen?`)) return;
        
        setLoading(true);
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.delete(`${API_URL}/users/${profile._id}`, config);
            
            setMessage({ type: 'success', text: 'Benutzer wurde erfolgreich gelöscht!' });
            
            // Redirect back to user list after a short delay
            setTimeout(() => {
                navigate('/admin-tools');
            }, 2000);
        } catch (error) {
            setMessage({ 
                type: 'error', 
                text: error.response?.data?.message || 'Fehler beim Löschen des Benutzers' 
            });
            setLoading(false);
        }
    };

    // --- Photo Upload Handlers ---
    const updateUserDataOnServer = async (partialUserData) => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const updateUrl = isAdminView 
                ? `${API_URL}/users/${profile._id}/profile` 
                : `${API_URL}/users/profile`;
                
            const { data } = await axios.put(updateUrl, {
                userData: {
                    ...profile.userData,
                    ...partialUserData
                }
            }, config);
            if (onUpdate) onUpdate(data);
            return true;
        } catch (error) {
            console.error("Failed to update user data", error);
            setMessage({ type: 'error', text: 'Fehler beim Speichern der Daten.' });
            return false;
        }
    };

    const handleProfilePhotoUpload = async (file) => {
        if (!file) return;
        setUploading(true);

        try {
            const uploadFormData = new FormData();
            uploadFormData.append('file', file);

            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${user.token}`
                }
            };
            const uploadRes = await axios.post(`${API_URL}/files/upload`, uploadFormData, config);
            const imageUrl = uploadRes.data.url || uploadRes.data.location || uploadRes.data;

            setProfilePhotoUrl(imageUrl);
            await updateUserDataOnServer({ profilePhoto: { url: imageUrl } });
            setMessage({ type: 'success', text: 'Profilbild erfolgreich hochgeladen!' });
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        } catch (err) {
            console.error(err);
            setMessage({ type: 'error', text: 'Fehler beim Hochladen.' });
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleProfilePhotoDelete = async () => {
        if (!window.confirm("Bist du sicher, dass du dein Profilbild löschen möchtest?")) return;

        setProfilePhotoUrl('');
        await updateUserDataOnServer({ profilePhoto: { url: '' } });
        setMessage({ type: 'success', text: 'Profilbild gelöscht.' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    };

    const handleGalleryUpload = async (file) => {
        if (!file) return;
        setUploadingGallery(true);

        try {
            const uploadFormData = new FormData();
            uploadFormData.append('file', file);

            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${user.token}`
                }
            };
            const uploadRes = await axios.post(`${API_URL}/files/upload`, uploadFormData, config);
            const imageUrl = uploadRes.data.url || uploadRes.data.location || uploadRes.data;

            const newPhotos = [...photos, { url: imageUrl }];
            setPhotos(newPhotos);
            await updateUserDataOnServer({ photos: newPhotos });
            setMessage({ type: 'success', text: 'Foto zur Galerie hinzugefügt!' });
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        } catch (err) {
            console.error(err);
            setMessage({ type: 'error', text: 'Fehler beim Hochladen.' });
        } finally {
            setUploadingGallery(false);
            if (galleryInputRef.current) galleryInputRef.current.value = "";
        }
    };

    const handleGalleryDelete = async (index) => {
        if (!window.confirm("Dieses Foto aus der Galerie löschen?")) return;

        const newPhotos = [...photos];
        newPhotos.splice(index, 1);
        setPhotos(newPhotos);
        await updateUserDataOnServer({ photos: newPhotos });
    };

    // Get the current display image (Photo priority > Avatar)
    const displayProfilePhoto = profilePhotoUrl || (photos && photos.length > 0 ? photos[0].url : '');

    const avatarUrl = profile.avatarUrl || profile.userData?.avatarConfig?.modelUrl;

    return (
        <div className="space-y-8 max-w-4xl mx-auto">
            {/* Global Alert */}
            {message.text && (
                <div className={`p-5 rounded-2xl flex items-center gap-4 animate-scale-in border ${message.type === 'success'
                    ? 'bg-primary-container/20 text-primary border-primary/20'
                    : 'bg-error-container/20 text-error border-error/20'
                    }`}>
                    {message.type === 'success' ? <CheckCircle size={24} /> : <AlertCircle size={24} />}
                    <span className="font-bold text-lg">{message.text}</span>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Avatar & Quick Info */}
                <div className="lg:col-span-1 space-y-8">
                    {/* Photo & Avatar Card */}
                    <div className="md3-card-elevated overflow-hidden border border-outlineVariant/30 !bg-surface-containerLow">
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) => e.target.files?.[0] && handleProfilePhotoUpload(e.target.files[0])}
                        />

                        <div className="grid grid-cols-2 divide-x divide-outlineVariant/20">
                            <div className="p-4 flex flex-col items-center">
                                <h4 className="text-label-large font-bold text-surface-on flex items-center gap-2 mb-3">
                                    <Camera size={14} />
                                    Foto
                                </h4>

                                {displayProfilePhoto ? (
                                    <div className="relative group w-full">
                                        <div className="aspect-square rounded-xl overflow-hidden border-2 border-primary/30 shadow-sm mx-auto max-w-[100px]">
                                            <img src={displayProfilePhoto} alt="Profilbild" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex justify-center gap-1 mt-2">
                                            <button
                                                type="button"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={uploading}
                                                className="md3-icon-btn w-8 h-8"
                                                title="Ändern"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleProfilePhotoDelete}
                                                className="md3-icon-btn w-8 h-8 !text-error"
                                                title="Löschen"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        className="aspect-square w-full max-w-[100px] rounded-xl border-2 border-dashed border-outlineVariant/60 bg-surface-container hover:bg-surface-containerHigh hover:border-primary flex flex-col items-center justify-center transition-all text-surface-onVariant gap-1"
                                    >
                                        {uploading ? (
                                            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <>
                                                <Upload size={20} />
                                                <span className="text-[10px]">Hochladen</span>
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>

                            <div className="p-4 flex flex-col items-center">
                                <h4 className="text-label-large font-bold text-surface-on flex items-center gap-2 mb-3">
                                    <Smile size={14} />
                                    Avatar
                                </h4>

                                {avatarUrl ? (
                                    <div className="relative group w-full">
                                        <div className="aspect-square rounded-xl overflow-hidden border-2 border-tertiary/30 shadow-sm mx-auto max-w-[100px] bg-surface-containerLowest">
                                            <img src={avatarUrl} alt="Avatar" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="flex justify-center mt-2">
                                            <Link
                                                to="/edit-avatar"
                                                className="md3-icon-btn w-8 h-8"
                                                title="Bearbeiten"
                                            >
                                                <Smile size={16} />
                                            </Link>
                                        </div>
                                    </div>
                                ) : (
                                    <Link
                                        to="/edit-avatar"
                                        className="aspect-square w-full max-w-[100px] rounded-xl border-2 border-dashed border-outlineVariant/60 bg-surface-container hover:bg-surface-containerHigh hover:border-tertiary flex flex-col items-center justify-center transition-all text-surface-onVariant gap-1"
                                    >
                                        <Smile size={20} />
                                        <span className="text-[10px]">Erstellen</span>
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Account Status Card */}
                    <div className="md3-card-outlined !bg-surface flex flex-col divide-y divide-outlineVariant/10">
                        <div className="p-6">
                            <h4 className="text-title-medium font-bold text-surface-on flex items-center gap-3">
                                <Shield size={20} className="text-primary" />
                                Account Status
                            </h4>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 text-[13px]">
                                <span className="text-surface-onVariant font-medium">Verifiziert</span>
                                {isAdminView && canManageRoles ? (
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.isEmailVerified}
                                            onChange={(e) => setFormData({ ...formData, isEmailVerified: e.target.checked })}
                                            className="w-4 h-4 accent-primary"
                                        />
                                        <span className={`px-2 py-0.5 rounded-lg font-bold ${formData.isEmailVerified ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`}>
                                            {formData.isEmailVerified ? 'JA' : 'NEIN'}
                                        </span>
                                    </label>
                                ) : (
                                    <span className={`px-2 py-0.5 rounded-lg font-bold w-fit ${profile.isEmailVerified ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`}>
                                        {profile.isEmailVerified ? 'JA' : 'NEIN'}
                                    </span>
                                )}
                            </div>

                            {formData.officialEmail && (
                                <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 text-[13px] bg-primary/5 p-2 rounded-xl border border-primary/10">
                                    <span className="text-primary font-bold">Offizielle Mail</span>
                                    <span className="font-bold text-primary break-all">{formData.officialEmail}</span>
                                </div>
                            )}
                            
                            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 text-[13px]">
                                <span className="text-surface-onVariant font-medium">Freigeschaltet</span>
                                {isAdminView && canManageRoles ? (
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.isApproved}
                                            onChange={(e) => setFormData({ ...formData, isApproved: e.target.checked })}
                                            className="w-4 h-4 accent-primary"
                                        />
                                        <span className={`px-2 py-0.5 rounded-lg font-bold ${formData.isApproved ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`}>
                                            {formData.isApproved ? 'JA' : 'NEIN'}
                                        </span>
                                    </label>
                                ) : (
                                    <span className={`px-2 py-0.5 rounded-lg font-bold w-fit ${profile.isApproved ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}`}>
                                        {profile.isApproved ? 'JA' : 'NEIN'}
                                    </span>
                                )}
                            </div>

                            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 text-[13px]">
                                <span className="text-surface-onVariant font-medium">Rolle</span>
                                {isAdminView && canManageRoles ? (
                                    <select
                                        name="role"
                                        value={formData.role}
                                        onChange={handleChange}
                                        className="bg-surface-containerHigh border border-outlineVariant rounded px-2 py-1 font-bold text-primary focus:outline-none"
                                    >
                                        <option value="kind">Kind</option>
                                        <option value="gruppenleiter">Gruppenleiter</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                ) : (
                                    <span className="px-2 py-0.5 rounded-lg bg-primary/10 text-primary font-bold uppercase tracking-wider">
                                        {profile.role}
                                    </span>
                                )}
                            </div>

                            <div className="flex flex-col justify-between gap-2 text-[13px] pt-2 border-t border-outlineVariant/10">
                                <span className="text-surface-onVariant font-medium">Warte-Funktionen</span>
                                {isAdminView && canManageRoles ? (
                                    <div className="flex flex-wrap gap-2">
                                        <label className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-surface-containerHigh border border-outlineVariant/30 text-xs font-semibold cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.warte?.includes('kassenwart')}
                                                onChange={(e) => {
                                                    const current = formData.warte || [];
                                                    const next = e.target.checked
                                                        ? [...current, 'kassenwart']
                                                        : current.filter(w => w !== 'kassenwart');
                                                    setFormData({ ...formData, warte: next });
                                                }}
                                                className="w-3.5 h-3.5 accent-primary rounded"
                                            />
                                            💰 Kassenwart
                                        </label>
                                        <label className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-surface-containerHigh border border-outlineVariant/30 text-xs font-semibold cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={formData.warte?.includes('materialwart')}
                                                onChange={(e) => {
                                                    const current = formData.warte || [];
                                                    const next = e.target.checked
                                                        ? [...current, 'materialwart']
                                                        : current.filter(w => w !== 'materialwart');
                                                    setFormData({ ...formData, warte: next });
                                                }}
                                                className="w-3.5 h-3.5 accent-primary rounded"
                                            />
                                            🏕️ Materialwart
                                        </label>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-1.5">
                                        {(formData.warte || []).length > 0 ? (
                                            (formData.warte || []).map(w => (
                                                <span key={w} className="px-2 py-0.5 rounded-lg bg-primary/10 text-primary font-bold text-xs">
                                                    {w === 'kassenwart' ? '💰 Kassenwart' : w === 'materialwart' ? '🏕️ Materialwart' : w}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="text-xs text-surface-onVariant opacity-60">Keine Warte-Rolle</span>
                                        )}
                                    </div>
                                )}
                            </div>



                            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 text-[13px]">
                                <span className="text-surface-onVariant font-medium">Mitglied seit</span>
                                <span className="font-bold text-surface-on">
                                    {new Date(profile.createdAt || Date.now()).toLocaleDateString('de-DE')}
                                </span>
                            </div>
                        </div>

                        {/* Admin Only Actions */}
                        {isAdminView && canManageRoles && (
                            <div className="p-4 bg-primary/5 space-y-3">
                                <p className="text-[10px] font-bold text-primary uppercase tracking-widest px-2">Admin Aktionen</p>
                                <div className="space-y-2">
                                    <Link 
                                        to={`/admin-tools?user=${profile._id}`}
                                        className="flex items-center justify-between p-3 rounded-xl bg-surface-containerHigh hover:bg-surface-containerHighest transition-colors group"
                                    >
                                        <span className="text-body-small font-bold">Berechtigungen</span>
                                        <ChevronRight size={14} className="opacity-40 group-hover:translate-x-1 transition-transform" />
                                    </Link>
                                    <button 
                                        type="button"
                                        className="w-full flex items-center justify-between p-3 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary transition-colors group text-left"
                                        onClick={() => {
                                            generateStrongPassword();
                                            setShowAdminPasswordModal(true);
                                        }}
                                    >
                                        <div>
                                            <div className="text-body-small font-bold flex items-center gap-1.5">
                                                <Key size={14} />
                                                <span>Passwort manuell setzen</span>
                                            </div>
                                            <p className="text-[11px] opacity-70 mt-0.5">Direkt ändern (auch ohne E-Mail)</p>
                                        </div>
                                        <ChevronRight size={14} className="opacity-40 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                    <button 
                                        type="button"
                                        className="w-full flex items-center justify-between p-3 rounded-xl bg-surface-containerHigh hover:bg-surface-containerHighest text-surface-on transition-colors group text-left"
                                        onClick={handleAdminResetPassword}
                                    >
                                        <span className="text-body-small font-bold">Reset-Link per E-Mail</span>
                                        <Refresh size={14} className="opacity-40 group-hover:rotate-180 transition-transform duration-500" />
                                    </button>
                                    <button 
                                        type="button"
                                        className="w-full flex items-center justify-between p-3 rounded-xl bg-error/5 hover:bg-error/10 text-error transition-colors group text-left"
                                        onClick={handleDeleteUser}
                                        disabled={loading}
                                    >
                                        <span className="text-body-small font-bold">Benutzer löschen</span>
                                        <Trash2 size={14} className="opacity-40 group-hover:scale-110 transition-transform" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Cloudflare Status (Admin only) */}
                    {isAdminView && canManageRoles && (
                        <CloudflareUserStatus profile={profile} onUpdate={onUpdate} />
                    )}
                </div>

                {/* Right Column: Forms */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Profile Information Form */}
                    <div className="md3-card-elevated !bg-surface-containerLow overflow-hidden border border-outlineVariant/30">
                        <div className="p-8 border-b border-outlineVariant/20">
                            <h3 className="text-headline-small font-bold text-surface-on flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                                    <User size={24} />
                                </div>
                                Persönliche Daten
                            </h3>
                        </div>
                        <form onSubmit={handleProfileUpdate} className="p-8 space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <InputWrapper label="Vorname" icon={User} required>
                                    <input
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className={inputClasses}
                                        required
                                    />
                                </InputWrapper>
                                <InputWrapper label="Nachname" icon={User}>
                                    <input
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className={inputClasses}
                                    />
                                </InputWrapper>
                                <InputWrapper label="Pfadiname" icon={Shield}>
                                    <input
                                        name="scoutName"
                                        value={formData.scoutName}
                                        onChange={handleChange}
                                        className={inputClasses}
                                        disabled={!isLeaderOrAdmin}
                                        placeholder={!isLeaderOrAdmin ? "Nur Gruppenleiter können dies ändern" : ""}
                                    />
                                </InputWrapper>
                                <InputWrapper label="Aufgabe (Job)" icon={Users}>
                                    <input
                                        name="job"
                                        value={formData.job}
                                        onChange={handleChange}
                                        className={inputClasses}
                                        placeholder="z.B. Gruppenleiter, Materialwart..."
                                    />
                                </InputWrapper>
                                <InputWrapper label="E-Mail Adresse" icon={Mail} required>
                                    <input
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={inputClasses}
                                        required
                                    />
                                </InputWrapper>
                                <InputWrapper label="Offizielle Mail" icon={Mail}>
                                    <input
                                        name="officialEmail"
                                        type="email"
                                        value={formData.officialEmail}
                                        onChange={handleChange}
                                        className={inputClasses}
                                        placeholder="z.B. vorname.nachname@pfadfinder-wutoeschingen.de"
                                    />
                                </InputWrapper>
                                <InputWrapper label="Geburtsdatum" icon={Calendar}>
                                    <input
                                        name="birthDate"
                                        type="date"
                                        value={formData.birthDate}
                                        onChange={handleChange}
                                        className={inputClasses}
                                    />
                                </InputWrapper>
                                <InputWrapper label="Telefonnummer" icon={Phone}>
                                    <input
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className={inputClasses}
                                    />
                                </InputWrapper>
                            </div>

                            <div className="pt-8 border-t border-outlineVariant/20">
                                <h4 className="text-title-medium font-bold text-surface-on mb-6">Anschrift</h4>
                                <div className="grid grid-cols-4 gap-x-8 gap-y-8">
                                    <div className="col-span-3">
                                        <InputWrapper label="Straße" icon={MapPin}>
                                            <input
                                                name="street"
                                                value={formData.street}
                                                onChange={handleChange}
                                                className={inputClasses}
                                            />
                                        </InputWrapper>
                                    </div>
                                    <div className="col-span-1">
                                        <InputWrapper label="Nr.">
                                            <input
                                                name="houseNumber"
                                                value={formData.houseNumber}
                                                onChange={handleChange}
                                                className={inputClasses}
                                            />
                                        </InputWrapper>
                                    </div>
                                    <div className="col-span-1">
                                        <InputWrapper label="PLZ">
                                            <input
                                                name="zipCode"
                                                value={formData.zipCode}
                                                onChange={handleChange}
                                                className={inputClasses}
                                            />
                                        </InputWrapper>
                                    </div>
                                    <div className="col-span-3">
                                        <InputWrapper label="Ort" icon={MapPin}>
                                            <input
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                className={inputClasses}
                                            />
                                        </InputWrapper>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-8 border-t border-outlineVariant/20">
                                <h4 className="text-title-medium font-bold text-surface-on mb-6">Team-Mitgliedschaft</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="flex items-center gap-4 p-4 rounded-2xl bg-surface-containerLowest border border-outlineVariant/20">
                                        <input
                                            type="checkbox"
                                            id="isTeamMember"
                                            checked={formData.isTeamMember}
                                            onChange={(e) => setFormData({ ...formData, isTeamMember: e.target.checked })}
                                            className="w-6 h-6 accent-primary"
                                        />
                                        <label htmlFor="isTeamMember" className="text-body-medium font-bold text-surface-on cursor-pointer">
                                            Auf Teamseite anzeigen
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="md3-btn-filled px-8 py-4 gap-3 !rounded-[1.5rem]"
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-2 border-primary-on border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Save size={20} />
                                            <span>Änderungen speichern</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Password Change Form - Only show for self or if special permission exists */}
                    {!isAdminView && (
                        <div className="md3-card-elevated !bg-surface-containerLow overflow-hidden border border-outlineVariant/30">
                        <div className="p-8 border-b border-outlineVariant/20">
                            <h3 className="text-headline-small font-bold text-surface-on flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center text-secondary">
                                    <Lock size={24} />
                                </div>
                                Passwort ändern
                            </h3>
                        </div>
                        <form onSubmit={handlePasswordUpdate} className="p-8 space-y-8">
                            <div className="space-y-6">
                                <InputWrapper label="Aktuelles Passwort" icon={Lock}>
                                    <div className="relative">
                                        <input
                                            name="currentPassword"
                                            type={showCurrentPassword ? 'text' : 'password'}
                                            value={passwordData.currentPassword}
                                            onChange={handlePasswordChange}
                                            className={inputClasses}
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-surface-onVariant/60 hover:text-primary transition-colors"
                                        >
                                            {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                </InputWrapper>

                                <div className="h-px bg-outlineVariant/20 my-4" />

                                <InputWrapper label="Neues Passwort" icon={Lock}>
                                    <div className="relative">
                                        <input
                                            name="newPassword"
                                            type={showNewPassword ? 'text' : 'password'}
                                            value={passwordData.newPassword}
                                            onChange={handlePasswordChange}
                                            className={inputClasses}
                                            placeholder="Mind. 6 Zeichen"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-surface-onVariant/60 hover:text-primary transition-colors"
                                        >
                                            {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                </InputWrapper>
                                <InputWrapper label="Neues Passwort bestätigen" icon={Lock}>
                                    <div className="relative">
                                        <input
                                            name="confirmPassword"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={passwordData.confirmPassword}
                                            onChange={handlePasswordChange}
                                            className={inputClasses}
                                            placeholder="Gleich wie oben"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-surface-onVariant/60 hover:text-primary transition-colors"
                                        >
                                            {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                        </button>
                                    </div>
                                </InputWrapper>
                            </div>

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="md3-btn-tonal !text-secondary border-2 border-secondary/20 !bg-secondary-container/10 px-8 py-4 gap-3 !rounded-[1.5rem]"
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-2 border-secondary border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Shield size={20} />
                                            <span>Passwort aktualisieren</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                    )}

                    {/* SSO Connections */}
                    <div className="md3-card-elevated !bg-surface-containerLow overflow-hidden border border-outlineVariant/30">
                        <div className="p-8 border-b border-outlineVariant/20">
                            <h3 className="text-headline-small font-bold text-surface-on flex items-center gap-4">
                                <div className="w-10 h-10 rounded-xl bg-tertiary/10 flex items-center justify-center text-tertiary">
                                    <LinkIcon size={24} />
                                </div>
                                Verknüpfte Konten
                            </h3>
                        </div>
                        <div className="p-8">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-6 rounded-[2rem] bg-surface-containerLowest border border-outlineVariant/20">
                                <div className="flex items-center gap-6">
                                    <div className="w-16 h-16 rounded-2xl bg-[#4285F4]/10 flex items-center justify-center">
                                        <svg width="28" height="28" viewBox="0 0 24 24">
                                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285f4" />
                                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34a853" />
                                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#fbbc05" />
                                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#ea4335" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="text-title-large font-bold text-surface-on mb-1">Google</h4>
                                        <p className="text-body-medium text-surface-onVariant/70">
                                            {profile.ssoProvider === 'google'
                                                ? (profile.googleEmail ? `Verbunden mit ${profile.googleEmail}` : 'Verbunden')
                                                : 'Keine Verbindung'}
                                        </p>
                                    </div>
                                </div>

                                {profile.ssoProvider === 'google' ? (
                                    <div className="flex items-center gap-4">
                                        {unlinkGooglePending ? (
                                            <div className="flex items-center gap-3 text-error bg-error/10 px-4 py-2 rounded-xl border border-error/20">
                                                <AlertCircle size={18} />
                                                <span className="text-label-medium font-bold">Wird beim Speichern getrennt</span>
                                                <button 
                                                    onClick={() => setUnlinkGooglePending(false)}
                                                    className="text-[10px] underline ml-2"
                                                >
                                                    Rückgängig
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={async () => {
                                                    if (isAdminView) {
                                                        if (window.confirm('Verbindung zu Google wirklich trennen?')) {
                                                            setUnlinkGooglePending(true);
                                                        }
                                                    } else {
                                                        if (window.confirm('Verbindung zu Google wirklich trennen?')) {
                                                            try {
                                                                const config = { headers: { Authorization: `Bearer ${user.token}` } };
                                                                await axios.post(`${API_URL}/auth/unlink-google`, {}, config);
                                                                window.location.reload();
                                                            } catch (error) {
                                                                alert('Fehler beim Trennen');
                                                            }
                                                        }
                                                    }
                                                }}
                                                className="md3-btn-text !text-error !rounded-xl gap-2 hover:bg-error/10"
                                            >
                                                <Unlink size={18} />
                                                Trennen
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <button
                                        disabled={isAdminView}
                                        onClick={() => {
                                            if (!isAdminView) {
                                                window.location.href = `${API_URL}/auth/google?token=${user.token}`;
                                            }
                                        }}
                                        className={`md3-btn-tonal !rounded-xl gap-2 ${isAdminView ? 'opacity-50 cursor-not-allowed bg-surface-containerHigh text-surface-onVariant' : '!bg-[#4285F4]/10 !text-[#4285F4] hover:bg-[#4285F4]/20'}`}
                                    >
                                        <LinkIcon size={18} />
                                        {isAdminView ? 'Nicht verbunden' : 'Verbinden'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Admin Direct Password Setting Modal */}
            {showAdminPasswordModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
                    <div 
                        className="bg-surface-containerHigh text-surface-on w-full max-w-lg rounded-3xl p-6 md:p-8 shadow-2xl border border-outlineVariant/30 space-y-6 animate-scale-in"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between border-b border-outlineVariant/20 pb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                                    <Key size={20} />
                                </div>
                                <div>
                                    <h3 className="text-title-large font-bold">Passwort manuell setzen</h3>
                                    <p className="text-body-small text-surface-onVariant">
                                        Für {profile.userData?.firstName || profile.userData?.scoutName || profile.email}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowAdminPasswordModal(false)}
                                className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-surface-on/10 transition-colors"
                            >
                                <Close size={20} />
                            </button>
                        </div>

                        <div className="p-4 bg-primary/5 rounded-2xl border border-primary/10 text-body-small text-surface-onVariant space-y-1">
                            <p className="font-bold text-primary flex items-center gap-1.5">
                                <Shield size={16} />
                                Funktioniert ohne E-Mail-Versand
                            </p>
                            <p>
                                Das Passwort wird direkt in der Datenbank gespeichert. Du kannst das Passwort sofort kopieren und dem Benutzer persönlich oder per Chat mitteilen.
                            </p>
                        </div>

                        <form onSubmit={handleAdminDirectSetPassword} className="space-y-4">
                            <div>
                                <label className="block text-body-medium font-bold text-surface-onVariant mb-2">
                                    Neues Passwort
                                </label>
                                <div className="relative">
                                    <input
                                        type={showAdminPasswordText ? "text" : "password"}
                                        value={adminNewPassword}
                                        onChange={(e) => {
                                            setAdminNewPassword(e.target.value);
                                            setCopiedAdminPassword(false);
                                        }}
                                        placeholder="Mindestens 6 Zeichen"
                                        required
                                        className="w-full px-4 py-3.5 pr-24 rounded-2xl bg-surface-containerLowest border border-outlineVariant/40 text-surface-on font-mono text-body-large focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    />
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => setShowAdminPasswordText(!showAdminPasswordText)}
                                            className="p-2 text-surface-onVariant hover:text-surface-on rounded-xl hover:bg-surface-on/5 transition-colors"
                                            title={showAdminPasswordText ? "Verbergen" : "Anzeigen"}
                                        >
                                            {showAdminPasswordText ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (adminNewPassword) {
                                                    navigator.clipboard.writeText(adminNewPassword);
                                                    setCopiedAdminPassword(true);
                                                    setTimeout(() => setCopiedAdminPassword(false), 3000);
                                                }
                                            }}
                                            className="p-2 text-surface-onVariant hover:text-primary rounded-xl hover:bg-surface-on/5 transition-colors"
                                            title="In Zwischenablage kopieren"
                                        >
                                            {copiedAdminPassword ? <Check size={18} className="text-success" /> : <Save size={18} />}
                                        </button>
                                    </div>
                                </div>
                                {copiedAdminPassword && (
                                    <p className="text-xs text-primary font-bold mt-1.5 flex items-center gap-1">
                                        <Check size={14} /> In die Zwischenablage kopiert!
                                    </p>
                                )}
                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={generateStrongPassword}
                                    className="md3-btn-tonal !rounded-2xl gap-2 text-body-small flex-1 py-2.5"
                                >
                                    <Refresh size={16} />
                                    Neues Zufallspasswort generieren
                                </button>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-outlineVariant/20">
                                <button
                                    type="button"
                                    onClick={() => setShowAdminPasswordModal(false)}
                                    className="md3-btn-text !rounded-2xl px-5 py-3"
                                    disabled={loading}
                                >
                                    Abbrechen
                                </button>
                                <button
                                    type="submit"
                                    className="md3-btn-filled !rounded-2xl px-6 py-3 gap-2 font-bold shadow-md"
                                    disabled={loading || !adminNewPassword || adminNewPassword.length < 6}
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-2 border-surface-on border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Check size={18} />
                                            Passwort jetzt speichern
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileSettings;
